import { useCallback, useEffect, useRef, useState, type ChangeEvent, type ElementType, type ReactNode } from "react";
import {
  Badge, Box, Button, Divider, Flex, HStack, Icon, IconButton,
  Text, VStack, useColorModeValue,
} from "@chakra-ui/react";
import { ArrowBackIcon, CheckCircleIcon, TimeIcon } from "@chakra-ui/icons";
import { AlertCircle, CheckCircle2, ChevronRight, Clock, Download, History, Paperclip, Upload, X } from "lucide-react";

import { AdmissionDataPanel, type AdmissionDrafts, type AdmissionField } from "./AdmissionDataPanel";
import { getCandidateAudit, type HrProcessAuditEntry } from "../../../../lib/api";
import { formatAuditChange, formatDateTime } from "../hrAuditLabels";
import type { Candidate, CandidateDetail } from "../../../../types/onboarding";
import type { WhitelistEntry } from "../../../../types/whitelist";
import { OnflyLoading } from "../../../../tools/OnflyLoading";

type StepStatus = "completed" | "in_progress" | "not_started" | "pending";

const STATUS_VISUAL: Record<StepStatus, { icon: ElementType; iconColor: string; label: string; badge: string }> = {
  completed:    { icon: CheckCircleIcon, iconColor: "green.500",  label: "Concluído",         badge: "green"  },
  in_progress:  { icon: Clock,           iconColor: "yellow.500", label: "Em andamento",     badge: "yellow" },
  not_started:  { icon: TimeIcon,        iconColor: "gray.300",    label: "Não iniciado",     badge: "gray"   },
  pending:      { icon: AlertCircle,     iconColor: "red.500",     label: "Possui pendências", badge: "red"    },
};

interface StepDef {
  key: string;
  title: string;
  status?: StepStatus;
  customIcon?: ElementType;
  customColor?: string;
  customLabel?: string;
}

interface TaskCardProps {
  title: string;
  completed: boolean;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
}

/** Card de tarefa: ícone + título + status (badge) + descrição + ação no canto
 * inferior direito. Layout orientado a tarefas, não a formulário. */
const TaskCard = ({ title, completed, description, children, footer }: TaskCardProps) => {
  const cardBg     = useColorModeValue("white", "whiteAlpha.50");
  const cardBorder = useColorModeValue("gray.200", "whiteAlpha.100");
  const titleClr   = useColorModeValue("gray.700", "gray.200");
  const subClr     = useColorModeValue("gray.500", "gray.400");
  return (
    <Box p={4} bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="md">
      <Flex align="center" gap={2} mb={description || children ? 2 : 0}>
        <Icon as={completed ? CheckCircleIcon : TimeIcon}
          color={completed ? "green.500" : "orange.400"} boxSize={4} flexShrink={0} />
        <Text fontSize="sm" fontWeight="semibold" color={titleClr} flex={1}>{title}</Text>
        <Badge colorScheme={completed ? "green" : "orange"} variant="subtle" fontSize="xs" flexShrink={0}>
          {completed ? "Concluído" : "Pendente"}
        </Badge>
      </Flex>
      {description && <Text fontSize="sm" color={subClr} mb={children ? 3 : 0}>{description}</Text>}
      {children}
      {footer && <Flex justify="flex-end" gap={2} mt={3}>{footer}</Flex>}
    </Box>
  );
};

export interface RhProcessWorkflowProps {
  candidate: Candidate;
  whitelistEntry: WhitelistEntry | null;
  detail: CandidateDetail | null;
  token: string;
  // admission
  admissionDrafts: AdmissionDrafts;
  onDraftChange: (field: AdmissionField, value: string) => void;
  onSaveAdmission: () => Promise<void>;
  isSavingAdmission: boolean;
  hasAdmissionChanges: boolean;
  // exame — convocação (guia de agendamento)
  onSendExamConvocation: (file: File) => Promise<boolean>;
  isSendingExamConvocation: boolean;
  // exame — ASO (upload ao starter-pack; distinto da convocação)
  onUploadAso: (file: File) => void;
  isUploadingAso: boolean;
  // benefícios
  onSendBenefits: () => void;
  isSendingBenefitsEmail: boolean;
  // DP finalizado
  onSetDpFinished: (finished: boolean) => Promise<void>;
  isSettingDpFinished: boolean;
  /** Sem rh.salary.view, a etapa Salário não conta pro progresso da admissão. */
  canViewSalary?: boolean;
  /** Candidato PJ: a etapa "Exame admissional e convocação" não se aplica
   * (PJ não tem ASO/exame admissional) e não aparece no workflow. */
  isPj?: boolean;
  onRefresh?: () => void;
}

const ADMISSION_CHECKS = (w: WhitelistEntry | null, canViewSalary: boolean): boolean[] => [
  !!w?.responsible_manager,
  !!w?.job_title,
  !!w?.job_activities,
  ...(canViewSalary ? [!!w?.salary && w.salary !== ""] : []),
  !!w?.cost_center,
  !!w?.workplace,
  !!w?.work_schedule,
  !!w?.work_start_time && !!w?.work_end_time,
  !!w?.admission_date,
];

/** Resolve ícone/cor/label de uma etapa (pura — não depende do closure). */
function rowVisual(step: StepDef) {
  if (step.customIcon) return { icon: step.customIcon, iconColor: step.customColor ?? "blue.500", label: step.customLabel ?? "" };
  const v = STATUS_VISUAL[step.status ?? "not_started"];
  return { icon: v.icon, iconColor: v.iconColor, label: v.label };
}

// ---------------------------------------------------------------------------
// StepRow — extraído para o top-level para não ser recriado a cada render do
// parent (o que desmontaria os inputs e perderia o foco durante a digitação).
// ---------------------------------------------------------------------------
interface StepRowProps {
  step: StepDef;
  isLast: boolean;
  onSelect: (key: string) => void;
}
const StepRow = ({ step, isLast, onSelect }: StepRowProps) => {
  const v = rowVisual(step);
  const titleColor    = useColorModeValue("gray.800", "white");
  const muted         = useColorModeValue("gray.500", "gray.400");
  const rowBorderColor = useColorModeValue("gray.100", "whiteAlpha.200");
  const hoverBg       = useColorModeValue("gray.50", "whiteAlpha.100");

  const subLabel = step.customIcon
    ? `${v.label}`
    : step.status === "not_started" ? "Pendente — clique para iniciar" : `${v.label} — clique para abrir`;
  const isNeutral = !!step.customIcon || step.status === "not_started";
  return (
    <Flex
      align="center" gap={3} py={3}
      borderBottomWidth={isLast ? 0 : "1px"} borderColor={rowBorderColor}
      cursor="pointer" borderRadius="md" px={2} mx={-2}
      _hover={{ bg: hoverBg }}
      onClick={() => onSelect(step.key)}
      role="button"
    >
      <Icon as={v.icon} color={v.iconColor} boxSize={5} flexShrink={0} />
      <Box flex={1}>
        <Text fontSize="sm" fontWeight="medium" color={isNeutral ? titleColor : v.iconColor}>
          {step.title}
        </Text>
        <Text fontSize="xs" color={isNeutral ? muted : v.iconColor}>{subLabel}</Text>
      </Box>
      <Icon as={ChevronRight} boxSize={4} color={muted} flexShrink={0} />
    </Flex>
  );
};

// ---------------------------------------------------------------------------
// StepDetail — extraído para o top-level pelo mesmo motivo do StepRow.
// ---------------------------------------------------------------------------
interface StepDetailProps {
  step: StepDef;
  onBack: () => void;
  // admission
  admissionDrafts: AdmissionDrafts;
  onDraftChange: (field: AdmissionField, value: string) => void;
  onSaveAdmission: () => Promise<void>;
  isSavingAdmission: boolean;
  hasAdmissionChanges: boolean;
  canViewSalary: boolean;
  // exame — convocação (guia de agendamento)
  examInputRef: React.RefObject<HTMLInputElement>;
  onExamFileChange: (e: ChangeEvent<HTMLInputElement>) => void;
  examConvocationFile: File | null;
  onClearExamConvocationFile: () => void;
  onSendExam: () => void;
  isSendingExamConvocation: boolean;
  examConvocationSent: boolean;
  examConvocationSentAt: string | null;
  // exame — ASO (upload ao starter-pack; distinto da convocação)
  asoInputRef: React.RefObject<HTMLInputElement>;
  onAsoChange: (e: ChangeEvent<HTMLInputElement>) => void;
  isUploadingAso: boolean;
  asoCompleted: boolean;
  detail: CandidateDetail | null;
  // benefícios
  benefitsSent: boolean;
  benefitsSentAt: string | null;
  onSendBenefits: () => void;
  isSendingBenefitsEmail: boolean;
  // histórico
  auditEntries: HrProcessAuditEntry[];
  isLoadingAudit: boolean;
}

const StepDetail = ({
  step, onBack,
  admissionDrafts, onDraftChange, onSaveAdmission, isSavingAdmission, hasAdmissionChanges, canViewSalary,
  examInputRef, onExamFileChange, examConvocationFile, onClearExamConvocationFile,
  onSendExam, isSendingExamConvocation, examConvocationSent, examConvocationSentAt,
  asoInputRef, onAsoChange, isUploadingAso, asoCompleted, detail,
  benefitsSent, benefitsSentAt, onSendBenefits, isSendingBenefitsEmail,
  auditEntries, isLoadingAudit,
}: StepDetailProps) => {
  const v = rowVisual(step);
  const labelColor  = useColorModeValue("gray.700", "gray.200");
  const muted       = useColorModeValue("gray.500", "gray.400");
  const backBtnColor = useColorModeValue("gray.500", "whiteAlpha.600");
  const cardBorder  = useColorModeValue("gray.200", "whiteAlpha.100");
  const inputBg     = useColorModeValue("white", "gray.700");

  return (
    <VStack align="stretch" spacing={5}>
      <Button
        leftIcon={<ArrowBackIcon />} variant="ghost" size="sm" alignSelf="flex-start"
        onClick={onBack} color={backBtnColor} px={0}
        _hover={{ bg: "transparent", color: "blue.500" }}
      >
        Voltar para etapas
      </Button>

      <Box>
        <HStack spacing={2} mb={1}>
          <Icon as={v.icon} color={v.iconColor} boxSize={4} />
          <Text fontWeight="bold" fontSize="md">{step.title}</Text>
        </HStack>
        {!step.customIcon && (
          <Badge colorScheme={STATUS_VISUAL[step.status ?? "not_started"].badge} variant="subtle" fontSize="xs">
            {STATUS_VISUAL[step.status ?? "not_started"].label}
          </Badge>
        )}
      </Box>

      <Divider />

      {/* Etapa 1 — Dados da admissão */}
      {step.key === "admission" && (
        <VStack align="stretch" spacing={3}>
          <AdmissionDataPanel
            drafts={admissionDrafts}
            onDraftChange={onDraftChange}
            isSaving={isSavingAdmission}
            showHeader={false}
            canViewSalary={canViewSalary}
          />
          <Flex justify="flex-end">
            <Button
              size="sm" colorScheme="blue"
              onClick={onSaveAdmission}
              isDisabled={!hasAdmissionChanges || isSavingAdmission}
              isLoading={isSavingAdmission}
              loadingText="Salvando..."
            >
              Salvar
            </Button>
          </Flex>
        </VStack>
      )}

      {/* Etapa 2 — Exame admissional: convocação (guia de agendamento) e ASO */}
      {step.key === "exam" && (
        <VStack align="stretch" spacing={4}>
          <input
            ref={examInputRef} type="file"
            style={{ display: "none" }} onChange={onExamFileChange}
          />
          {/* Card: Convocação para exame — anexo da guia de agendamento.
              A guia contém todos os dados do agendamento; sem campos de preenchimento. */}
          <TaskCard
            title="Convocação para exame"
            completed={examConvocationSent}
            description={
              examConvocationSent
                ? `Convocação enviada${examConvocationSentAt ? ` em ${formatDateTime(examConvocationSentAt)}` : ""}. Você pode reenviar com uma nova guia.`
                : "Anexe a guia de agendamento (contém data, horário e clínica) e envie ao candidato."
            }
          >
            <HStack
              as="button" type="button"
              w="full" h="32px" px={3} justify="space-between"
              bg={inputBg} borderWidth="1px" borderColor={cardBorder} borderRadius="md"
              onClick={() => examInputRef.current?.click()}
            >
              <Text fontSize="sm" noOfLines={1} color={examConvocationFile ? labelColor : muted}>
                {examConvocationFile
                  ? examConvocationFile.name
                  : examConvocationSent
                    ? "Guia já enviada — selecionar para trocar"
                    : "Selecionar guia de agendamento..."}
              </Text>
              {examConvocationFile ? (
                <IconButton
                  aria-label="Remover guia selecionada" icon={<X size={12} />}
                  size="xs" variant="ghost" flexShrink={0}
                  onClick={(e) => { e.stopPropagation(); onClearExamConvocationFile(); }}
                />
              ) : (
                <Icon as={Paperclip} boxSize={3.5} color={muted} flexShrink={0} />
              )}
            </HStack>

            <Flex justify="flex-end" mt={3}>
              <Button
                size="sm" colorScheme="purple"
                onClick={onSendExam}
                isDisabled={!examConvocationFile}
                isLoading={isSendingExamConvocation}
                loadingText="Enviando..."
              >
                {examConvocationSent ? "Reenviar convocação" : "Enviar convocação"}
              </Button>
            </Flex>
          </TaskCard>

          <input
            ref={asoInputRef} type="file" accept=".pdf,application/pdf"
            style={{ display: "none" }} onChange={onAsoChange}
          />
          {/* Card: Upload do ASO — distinto da convocação com guia. */}
          <TaskCard
            title="Upload do ASO"
            completed={asoCompleted}
            description={
              asoCompleted
                ? "ASO enviado e exame confirmado. Você pode substituir o arquivo se necessário."
                : "Envie o arquivo ASO para confirmar a conclusão do exame admissional."
            }
            footer={
              <>
                {asoCompleted && detail?.aso_file_url && (
                  <Button as="a" href={detail.aso_file_url} target="_blank" rel="noreferrer"
                    size="sm" variant="outline" colorScheme="blue"
                    leftIcon={<Icon as={Download} boxSize={3} />}>
                    Visualizar ASO
                  </Button>
                )}
                <Button
                  size="sm" colorScheme="blue"
                  leftIcon={<Icon as={Upload} boxSize={3} />}
                  onClick={() => asoInputRef.current?.click()}
                  isLoading={isUploadingAso}
                  loadingText="Enviando..."
                >
                  {asoCompleted ? "Substituir ASO" : "Enviar ASO"}
                </Button>
              </>
            }
          />
        </VStack>
      )}

      {/* Etapa 3 — Benefícios */}
      {step.key === "benefits" && (
        <VStack align="stretch" spacing={4}>
          <TaskCard
            title="E-mail de benefícios"
            completed={benefitsSent}
            description={
              benefitsSent
                ? `Benefícios enviados${benefitsSentAt ? ` em ${formatDateTime(benefitsSentAt)}` : ""}.`
                : "E-mail de benefícios ainda não enviado ao candidato."
            }
            footer={
              <Button
                size="sm" colorScheme={benefitsSent ? "gray" : "blue"}
                variant={benefitsSent ? "outline" : "solid"}
                onClick={onSendBenefits}
                isLoading={isSendingBenefitsEmail}
                loadingText="Enviando..."
              >
                {benefitsSent ? "Reenviar" : "Enviar e-mail"}
              </Button>
            }
          />
        </VStack>
      )}

      {/* Etapa 4 — Histórico */}
      {step.key === "history" && (
        <VStack align="stretch" spacing={3} alignItems={isLoadingAudit ? "center" : ""} justifyContent={"center"} h={isLoadingAudit ? "50vh" : ""}>
          {isLoadingAudit ? (
            <OnflyLoading />
          ) : auditEntries.length === 0 ? (
            <Text fontSize="sm" color={muted}>Nenhuma alteração registrada.</Text>
          ) : (
            auditEntries.map((e) => (
              <Box key={e.id} borderWidth="1px" borderColor={cardBorder} borderRadius="md" p={3}>
                <Text fontSize="sm" fontWeight="semibold" color={labelColor}>
                  {e.actor_name || e.actor_email}
                </Text>
                <Text fontSize="xs" color={muted} mb={1}>{formatDateTime(e.created_at)}</Text>
                <Text fontSize="sm" color={muted}>{formatAuditChange(e)}</Text>
              </Box>
            ))
          )}
        </VStack>
      )}
    </VStack>
  );
};

export const RhProcessWorkflow = ({
  candidate, whitelistEntry, detail, token,
  admissionDrafts, onDraftChange, onSaveAdmission, isSavingAdmission, hasAdmissionChanges,
  onSendExamConvocation, isSendingExamConvocation,
  onUploadAso, isUploadingAso,
  onSendBenefits, isSendingBenefitsEmail,
  onSetDpFinished, isSettingDpFinished,
  canViewSalary = true,
  isPj = false,
}: RhProcessWorkflowProps) => {
  const asoInputRef = useRef<HTMLInputElement>(null);
  const examInputRef = useRef<HTMLInputElement>(null);
  const [examConvocationFile, setExamConvocationFile] = useState<File | null>(null);

  const [selectedStep, setSelectedStep] = useState<string | null>(null);
  const [auditEntries, setAuditEntries] = useState<HrProcessAuditEntry[]>([]);
  const [isLoadingAudit, setIsLoadingAudit] = useState(false);

  const labelColor     = useColorModeValue("gray.700", "gray.200");
  const muted          = useColorModeValue("gray.500", "gray.400");
  const dpDividerColor = useColorModeValue("gray.100", "whiteAlpha.200");

  // ── status das etapas ──────────────────────────────────────────────────────
  const admissionFilled = ADMISSION_CHECKS(whitelistEntry, canViewSalary).filter(Boolean).length;
  const admissionTotal = ADMISSION_CHECKS(whitelistEntry, canViewSalary).length;
  const examConvocationSent = !!whitelistEntry?.exam_email_sent_at;
  const asoCompleted = !!whitelistEntry?.admission_exam_completed;
  const benefitsSent = !!whitelistEntry?.benefits_email_sent;

  const step1: StepStatus = admissionFilled === admissionTotal ? "completed" : admissionFilled === 0 ? "not_started" : "in_progress";
  // Etapa do exame conclui com as duas ações: convocação (guia) enviada + ASO enviado.
  const examDone = [asoCompleted, examConvocationSent].filter(Boolean).length;
  const step2: StepStatus = examDone === 2 ? "completed" : examDone === 0 ? "not_started" : "in_progress";
  const step3: StepStatus = benefitsSent ? "completed" : "not_started";

  // ── histórico ──────────────────────────────────────────────────────────────
  const loadAudit = useCallback(async () => {
    if (!token || !candidate.email) return;
    setIsLoadingAudit(true);
    try {
      setAuditEntries(await getCandidateAudit(token, candidate.email, 30));
    } catch {
      setAuditEntries([]);
    } finally {
      setIsLoadingAudit(false);
    }
  }, [token, candidate.email]);

  useEffect(() => {
    if (selectedStep === "history") void loadAudit();
  }, [selectedStep, loadAudit]);

  // ── ações ───────────────────────────────────────────────────────────────────
  const handleExamFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    setExamConvocationFile(e.target.files?.[0] ?? null);
    e.target.value = "";
  };

  // O drawer fica montado entre aberturas — limpa o arquivo ao mudar de
  // candidato para não enviar a guia de um para o outro.
  useEffect(() => { setExamConvocationFile(null); }, [candidate]);

  // No sucesso, limpa o anexo selecionado e volta à lista de etapas.
  const handleSendExam = async () => {
    if (!examConvocationFile) return;
    const ok = await onSendExamConvocation(examConvocationFile);
    if (ok) {
      setSelectedStep(null);
      setExamConvocationFile(null);
    }
  };

  const handleAsoChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onUploadAso(file);
    e.target.value = "";
  };

  const mainSteps: StepDef[] = [
    { key: "admission", title: "Dados da admissão", status: step1 },
    // PJ não tem ASO/exame admissional — a etapa não aparece no workflow.
    ...(isPj ? [] : [{ key: "exam", title: "Exame admissional e convocação", status: step2 }]),
    { key: "benefits",  title: "Benefícios", status: step3 },
  ];
  const historyStep: StepDef = { key: "history", title: "Histórico", customIcon: History, customColor: "blue.500", customLabel: "Ver alterações" };
  const steps: StepDef[] = [...mainSteps, historyStep];

  const selected = steps.find((s) => s.key === selectedStep);

  return (
    <VStack align="stretch" spacing={4}>
      {/* Etapas (lista) ou detalhe da etapa selecionada */}
      {selected ? (
        <StepDetail
          step={selected}
          onBack={() => setSelectedStep(null)}
          admissionDrafts={admissionDrafts}
          onDraftChange={onDraftChange}
          onSaveAdmission={onSaveAdmission}
          isSavingAdmission={isSavingAdmission}
          hasAdmissionChanges={hasAdmissionChanges}
          canViewSalary={canViewSalary}
          examInputRef={examInputRef}
          onExamFileChange={handleExamFileChange}
          examConvocationFile={examConvocationFile}
          onClearExamConvocationFile={() => setExamConvocationFile(null)}
          onSendExam={handleSendExam}
          isSendingExamConvocation={isSendingExamConvocation}
          examConvocationSent={examConvocationSent}
          examConvocationSentAt={whitelistEntry?.exam_email_sent_at ?? null}
          asoInputRef={asoInputRef}
          onAsoChange={handleAsoChange}
          isUploadingAso={isUploadingAso}
          asoCompleted={asoCompleted}
          detail={detail}
          benefitsSent={benefitsSent}
          benefitsSentAt={whitelistEntry?.benefits_email_sent_at ?? null}
          onSendBenefits={onSendBenefits}
          isSendingBenefitsEmail={isSendingBenefitsEmail}
          auditEntries={auditEntries}
          isLoadingAudit={isLoadingAudit}
        />
      ) : (
        <Box>
          <Flex align="center" justify="space-between" mb={1}>
            <Text fontSize="sm" fontWeight="semibold" color={labelColor}>Etapas</Text>
          </Flex>
          <Text fontSize="xs" color={muted} mb={3}>Clique em uma etapa para preencher.</Text>
          <Box>
            {mainSteps.map((step) => (
              <StepRow key={step.key} step={step} isLast={false} onSelect={setSelectedStep} />
            ))}

            {/* DP finalizado — entre Benefícios e Histórico */}
            <Flex
              align="center" gap={3} py={3} px={2} mx={-2}
              borderBottomWidth="1px" borderColor={dpDividerColor}
            >
              <Icon
                as={whitelistEntry?.dp_finished ? CheckCircle2 : Clock}
                boxSize={5}
                color={whitelistEntry?.dp_finished ? "green.500" : "yellow.500"}
                flexShrink={0}
              />
              <Box flex={1}>
                <Text fontSize="sm" fontWeight="medium" color={whitelistEntry?.dp_finished ? "green.500" : "yellow.500"}>
                  {whitelistEntry?.dp_finished ? "DP finalizado" : "DP pendente"}
                </Text>
                <Text fontSize="xs" color={whitelistEntry?.dp_finished ? "green.500" : "yellow.500"}>
                  {whitelistEntry?.dp_finished ? "Concluído" : "Pendente — marque quando o DP finalizar"}
                </Text>
              </Box>
              <Button
                size="xs"
                colorScheme={whitelistEntry?.dp_finished ? "gray" : "green"}
                variant={whitelistEntry?.dp_finished ? "outline" : "solid"}
                isLoading={isSettingDpFinished}
                onClick={() => onSetDpFinished(!whitelistEntry?.dp_finished)}
                flexShrink={0}
              >
                {whitelistEntry?.dp_finished ? "Desmarcar DP" : "Marcar DP como finalizado"}
              </Button>
            </Flex>

            <StepRow key={historyStep.key} step={historyStep} isLast={true} onSelect={setSelectedStep} />
          </Box>
        </Box>
      )}
    </VStack>
  );
};